import { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  createColumnHelper,
  SortingState,
  ColumnFiltersState,
  flexRender,
} from "@tanstack/react-table";
import { apiClient } from "../services/api";
import { buttonStyle, buttonBackgroundColor } from "../components/Style";
import PageLayout from "../components/PageLayout";
import { Class } from "../types/Class";
import Dropdown from "../components/Dropdown";
import Badge from "../components/Badge";
import {
  tanstackTableStyle,
  getTanstackThStyle,
  sortHeaderStyle,
  sortArrowStyle,
  filterContainerStyle,
  tanstackTrStyle,
  getTanstackTdStyle,
  clickableCellStyle,
  emptyRowStyle,
} from "../components/Style";

const columnHelper = createColumnHelper<Class>();

const DAYS = [
  { key: "monday", label: "월", startKey: "mondayStartTime", endKey: "mondayEndTime" },
  { key: "tuesday", label: "화", startKey: "tuesdayStartTime", endKey: "tuesdayEndTime" },
  { key: "wednesday", label: "수", startKey: "wednesdayStartTime", endKey: "wednesdayEndTime" },
  { key: "thursday", label: "목", startKey: "thursdayStartTime", endKey: "thursdayEndTime" },
  { key: "friday", label: "금", startKey: "fridayStartTime", endKey: "fridayEndTime" },
  { key: "saturday", label: "토", startKey: "saturdayStartTime", endKey: "saturdayEndTime" },
  { key: "sunday", label: "일", startKey: "sundayStartTime", endKey: "sundayEndTime" },
] as const;

function SpecialClassManagementPage() {
  const [classes, setClasses] = useState<Class[]>([]);
  const [classNameOptions, setClassNameOptions] = useState<string[]>([]);
  const [teacherOptions, setTeacherOptions] = useState<string[]>([]);
  const [classRoomOptions, setClassRoomOptions] = useState<string[]>([]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const navigate = useNavigate();

  // API에서 반 목록 가져오기
  const fetchClasses = async () => {
    try {
      const data = await apiClient.getClasses();
      // 특강반만 필터링
      const specialClasses = data.filter((c: Class) => c.classCategory === "special");
      setClasses(specialClasses);
      const uniqueNames = Array.from(
        new Set((specialClasses || []).map((c: Class) => c.className).filter(Boolean))
      );
      setClassNameOptions(uniqueNames as string[]);
    } catch (error) {
      console.error("반 목록 조회 실패:", error);
    }
  };

  const fetchMeta = async () => {
    try {
      const [teachers, classrooms] = await Promise.all([
        apiClient.getTeachingTeachers(),
        apiClient.getClassClassrooms(),
      ]);
      setTeacherOptions((teachers || []).map((t: any) => t.username).filter(Boolean));
      setClassRoomOptions((classrooms || []).map((c: any) => c.classroomName).filter(Boolean));
    } catch (error) {
      console.error("메타데이터 조회 실패:", error);
    }
  };

  useEffect(() => {
    fetchClasses();
    fetchMeta();
  }, []);

  // 클래스의 가장 빠른 시작 시각, 월요일 유무, 반 이름 계산
  const getClassSortKey = (c: Class): [number, boolean, string] => {
    let mondayTuesdayMinTime = Infinity;
    let hasMonday = false;

    if (c.monday) {
      const timeStr = (c.mondayStartTime as string) || "23:59:59";
      const [h, m, s] = timeStr.split(":").map(Number);
      const timeInSeconds = h * 3600 + m * 60 + s;
      mondayTuesdayMinTime = Math.min(mondayTuesdayMinTime, timeInSeconds);
      hasMonday = true;
    }

    if (c.tuesday) {
      const timeStr = (c.tuesdayStartTime as string) || "23:59:59";
      const [h, m, s] = timeStr.split(":").map(Number);
      const timeInSeconds = h * 3600 + m * 60 + s;
      mondayTuesdayMinTime = Math.min(mondayTuesdayMinTime, timeInSeconds);
    }

    return [mondayTuesdayMinTime, hasMonday, c.className || ""];
  };

  // 기본 정렬: 월/화 시작 시각 → 월요일 유무 → 반 이름 순
  const sortedClasses = useMemo(() => {
    return [...classes].sort((a, b) => {
      const [aTime, aHasMonday, aName] = getClassSortKey(a);
      const [bTime, bHasMonday, bName] = getClassSortKey(b);

      // 1. 월/화 시작 시각 비교 (빠른 순)
      if (aTime !== bTime) {
        return aTime - bTime;
      }

      // 2. 월요일 유무 비교 (월요일 있는 것 우선)
      if (aHasMonday !== bHasMonday) {
        return aHasMonday ? -1 : 1;
      }

      // 3. 반 이름 사전순 (ABC/가나다)
      return aName.localeCompare(bName, "ko-KR");
    });
  }, [classes]);

  // 24시간 형식을 12시간 형식으로 변환
  const formatTo12Hour = (time24: string): string => {
    if (!time24 || time24 === "00:00") return "-";
    const [hour, minute] = time24.split(":").map(Number);
    const isPM = hour >= 12;
    let hour12 = hour % 12;
    if (hour12 === 0) hour12 = 12;
    const period = isPM ? "오후" : "오전";
    return `${period} ${String(hour12).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
  };

  // 같은 시간대의 요일들을 그룹핑
  const groupSchedulesByTime = useCallback((c: Class) => {
    const scheduleMap = new Map<string, string[]>();

    DAYS.forEach((day) => {
      const dayEnabled = (c[day.key as keyof Class] as boolean) ?? false;
      if (!dayEnabled) return;

      const startKey = day.startKey as keyof Class;
      const endKey = day.endKey as keyof Class;
      const startTime = ((c[startKey] as string) || "00:00").substring(0, 5);
      const endTime = ((c[endKey] as string) || "00:00").substring(0, 5);
      const timeRange = `${formatTo12Hour(startTime)} ~ ${formatTo12Hour(endTime)}`;

      if (!scheduleMap.has(timeRange)) {
        scheduleMap.set(timeRange, []);
      }
      scheduleMap.get(timeRange)!.push(day.label);
    });

    return Array.from(scheduleMap.entries());
  }, []);

  // 요일 배지 variant 결정
  const getDayBadgeVariant = (dayLabel: string) => {
    if (dayLabel === "토") return { backgroundColor: "#e3f2fd", textColor: "#1565c0", borderColor: "#90caf9" };
    if (dayLabel === "일") return { backgroundColor: "#ffebee", textColor: "#c62828", borderColor: "#ef9a9a" };
    return { variant: "default" as const };
  };

  // 테이블 컬럼 정의
  const columns = useMemo(() => [
    columnHelper.accessor("className", {
      header: "반 이름",
      size: 150,
      cell: (info) => (
        <span
          style={clickableCellStyle}
          onClick={() => navigate(`/class/special/${info.row.original.id}`)}
        >
          {info.getValue()}
        </span>
      ),
      sortingFn: "alphanumeric",
      filterFn: "equalsString",
    }),
    columnHelper.accessor("teacher", {
      header: "담당 선생님",
      size: 70,
      cell: (info) => (
        <span
          style={clickableCellStyle}
          onClick={() => navigate(`/class/special/${info.row.original.id}`)}
        >
          {info.getValue()}
        </span>
      ),
      sortingFn: "alphanumeric",
      filterFn: "equalsString",
    }),
    columnHelper.accessor("classRoom", {
      header: "교실",
      size: 100,
      cell: (info) => (
        <span
          style={clickableCellStyle}
          onClick={() => navigate(`/class/special/${info.row.original.id}`)}
        >
          {info.getValue() || "-"}
        </span>
      ),
      sortingFn: "alphanumeric",
      filterFn: "equalsString",
    }),
    columnHelper.display({
      id: "schedule",
      header: "수업시간",
      size: 400,
      cell: (info) => {
        const c = info.row.original;
        const schedules = groupSchedulesByTime(c);
        if (schedules.length === 0) {
          return <span style={{ color: "#999" }}>-</span>;
        }

        return (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 8,
              cursor: "pointer",
              fontSize: 13,
            }}
            onClick={() => navigate(`/class/special/${c.id}`)}
          >
            {schedules.map(([timeRange, days], idx) => (
              <div key={idx} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <div>
                  {days.map((day) => {
                    const badgeProps = getDayBadgeVariant(day);
                    return (
                      <Badge key={day} {...badgeProps} style={{ marginRight: 4 }}>
                        {day}
                      </Badge>
                    );
                  })}
                </div>
                <span style={{ color: "#555" }}>{timeRange}</span>
              </div>
            ))}
          </div>
        );
      },
    }),
    columnHelper.accessor("classStartDate", {
      header: "특강 시작일",
      size: 120,
      cell: (info) => (
        <span
          style={clickableCellStyle}
          onClick={() => navigate(`/class/special/${info.row.original.id}`)}
        >
          {info.getValue() || "-"}
        </span>
      ),
      sortingFn: "alphanumeric",
    }),
    columnHelper.accessor("classEndDate", {
      header: "특강 종료일",
      size: 120,
      cell: (info) => (
        <span
          style={clickableCellStyle}
          onClick={() => navigate(`/class/special/${info.row.original.id}`)}
        >
          {info.getValue() || "-"}
        </span>
      ),
      sortingFn: "alphanumeric",
    }),
    columnHelper.display({
      id: "status",
      header: "강의 상태",
      size: 100,
      cell: (info) => {
        const c = info.row.original;
        const getStatus = () => {
          if (!c.classStartDate || !c.classEndDate) return "정보 없음";
          const today = new Date();
          const startDate = new Date(c.classStartDate);
          const endDate = new Date(c.classEndDate);

          if (today < startDate) return "시작 예정";
          if (today >= startDate && today <= endDate) return "진행중";
          return "종료됨";
        };

        const status = getStatus();
        const statusColor = status === "시작 예정" ? "#ff9800" : status === "진행중" ? "#4caf50" : "#999";

        return (
          <span
            style={{
              cursor: "pointer",
              color: statusColor,
              fontWeight: 600,
            }}
            onClick={() => navigate(`/class/special/${c.id}`)}
          >
            {status}
          </span>
        );
      },
    }),
  ], [navigate, groupSchedulesByTime]);

  // Table instance
  const table = useReactTable({
    data: sortedClasses,
    columns,
    state: {
      sorting,
      columnFilters,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  return (
    <PageLayout
      title="특강반 관리"
      actionRows={[
        (
          <button
            key="add-class"
            onClick={() => navigate("/class/special/new")}
            style={{ ...buttonStyle, ...buttonBackgroundColor.save }}
          >
            + 반 추가
          </button>
        ),
      ]}
    >
      <style>
        {`
          .special-class-table tbody tr:hover {
            background-color: #f6f8fa;
          }
        `}
      </style>
      <div style={{ overflow: "auto", maxHeight: "calc(100vh - 180px)" }}>
        <table className="special-class-table" style={tanstackTableStyle}>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const columnId = header.column.id;
                  
                  return (
                    <th
                      key={header.id}
                      style={getTanstackThStyle(false, header.getSize(), {})}
                    >
                      {header.isPlaceholder ? null : (
                        <div>
                          {/* Header with sort */}
                          {header.column.getCanSort() ? (
                            <div
                              onClick={header.column.getToggleSortingHandler()}
                              style={sortHeaderStyle}
                            >
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                              {{
                                asc: <span style={sortArrowStyle}>▲</span>,
                                desc: <span style={sortArrowStyle}>▼</span>,
                              }[header.column.getIsSorted() as string] ?? null}
                            </div>
                          ) : (
                            flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )
                          )}
                          
                          {/* Filter row */}
                          <div style={filterContainerStyle}>
                            {columnId === "className" && (
                              <Dropdown
                                value={(header.column.getFilterValue() as string) ?? ""}
                                onChange={(value) => header.column.setFilterValue(value || undefined)}
                                options={[
                                  { value: "", label: "전체" },
                                  ...classNameOptions.map((v) => ({ value: v, label: v })),
                                ]}
                              />
                            )}
                            {columnId === "teacher" && (
                              <Dropdown
                                value={(header.column.getFilterValue() as string) ?? ""}
                                onChange={(value) => header.column.setFilterValue(value || undefined)}
                                options={[
                                  { value: "", label: "전체" },
                                  ...teacherOptions.map((v) => ({ value: v, label: v })),
                                ]}
                              />
                            )}
                            {columnId === "classRoom" && (
                              <Dropdown
                                value={(header.column.getFilterValue() as string) ?? ""}
                                onChange={(value) => header.column.setFilterValue(value || undefined)}
                                options={[
                                  { value: "", label: "전체" },
                                  ...classRoomOptions.map((v) => ({ value: v, label: v })),
                                ]}
                              />
                            )}
                          </div>
                        </div>
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  style={emptyRowStyle}
                >
                  특강반이 없습니다.
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr key={row.id} style={tanstackTrStyle}>
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      style={getTanstackTdStyle(false, cell.column.getSize())}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </PageLayout>
  );
}

export default SpecialClassManagementPage;
